/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
    bool, array,
    func, object,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { uid } from 'react-uid';
import Slider from 'react-slick';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector, useDispatch } from 'react-redux';
import Media from 'react-media';
import BYOItemsSelected from './BYOItemsSelected';
import style from './BYOGroupSlider.module.css';
import Dialog from '../../../../../UniversalTemplate/Dialog/Dialog';
import BYOItemDetails from './BYOItemDetails';
import byoTheme from './BYOTheme';
import { getBrand, getFeatureFlags } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import LinearProgressBar from './LinearProgressBar';
import { setSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Actions';
import useSSRMediaQuery from '../../../../../../../helpers/hooks/useSSRMediaQuery';
import useSalesforcePersonalizedContentQuery from '../../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import GenericSkeleton from '../../../../../GraphqlSkeletonComponents/GenericSkeleton';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const BYOGroupSlider = ({
    groupData,
    handleItemSelection,
    selectedItems,
    disabled,
    error,
    handleRemoveError,
    maxGroupNumber,
    selectedItem,
}) => {
    useStyles(style);
    const sliderRef = useRef(null);
    const [open, setModelOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const onClose = () => setModelOpen(false);
    const [selectedOption, setSelectedOption] = useState({});
    const brandData = useSelector(getBrand);
    const brandCode = brandData?.code;
    const featureFlags = useSelector(getFeatureFlags);
    const selectedBYODispatch = useDispatch();
    const deviceType = useSSRMediaQuery();
    const pageType = useSelector(getCurrentPageType);
    const isCyoQtyAbTestEnabled = featureFlags['is-cyo-pdp-step2-default-qty-button-ab-test-enabled'];
    const isCyoQtyForceEnabled = featureFlags['is-force-cyo-pdp-step2-default-qty-button-variant'];
    const { loading, variables, data } = useSalesforcePersonalizedContentQuery({
        queryName: 'CyoQuantityEnabled',
        interactionName: `${brandData?.domain} - Get Campaign - CYO-PDP-V1`,
        featureFlag: isCyoQtyAbTestEnabled && !isCyoQtyForceEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const isCYOQtyButtonEnabled = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isCYODefaultQtyButtonEnabled = isCYOQtyButtonEnabled?.toLowerCase() === 'test' || isCyoQtyForceEnabled;
    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    const setOpen = (e, toggle) => {
        if (isDragging) {
            e.stopPropagation();
            e.preventDefault();
        } else {
            setModelOpen(toggle);
        }
        setIsDragging(false);
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.clientX);
    };

    const handleMouseUp = (e) => {
        const distance = Math.abs(e.clientX - startX);
        if (distance > 5) {  // 5 is a threshold, adjust as needed
            setIsDragging(true);
        } else {
            setIsDragging(false);
        }
    };

    // default color assigned when no brand code found
    const {
        buttonColorSelect = '#512C1C',
    } = byoTheme[brandCode] || {};

    const renderDialog = () => (
        <BYOItemDetails
            selectedOption={selectedOption}
            open={open}
            setOpen={setOpen}
            handleItemSelection={(increment, isInput) => handleItemSelection(groupData, selectedOption.legacyId, selectedOption.name, selectedOption.id, increment, isInput)}
            numberSelected={selectedItems[selectedOption.legacyId]?.quantity || 0}
            disabled={disabled}
            error={error[selectedOption.legacyId]}
            handleRemoveError={handleRemoveError}
            isCYOQtyButtonEnabled={isCYODefaultQtyButtonEnabled}
        />
    );

    const removeItem = (name) => {
        const newAddItems = { ...selectedItem.addItems };
        const removedItem = newAddItems[name];
        delete newAddItems[name];
        selectedBYODispatch(setSelectedBYOItems({ ...selectedItem, addItems: { ...newAddItems,  totalProductSelected: selectedItem.addItems.totalProductSelected - removedItem.groupProductSelected } }));
    };

    const clearSelection = () => (
        selectedItems?.groupProductSelected >= 1 && (
            <span
                className={style.clearSelection}
                component="span"
                variant="body2"
                color="textPrimary"
                onKeyDown={() => {}}
                tabIndex={-1}
                role="button"
                onClick={() => {
                    removeItem(groupData.name);
                }}
            >
                Clear Selections
            </span>
        )
    );
    const device = useSSRMediaQuery({
        desktop: 1024, // this layout is visible till 1024
    });

    const renderLayout = () => (
        groupData.components.map((component) => (
            <div className={style.scrollableSection} role="presentation" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                <BYOItemsSelected
                    key={uid(component.id)}
                    numberSelected={selectedItems[component.legacyId]?.quantity || 0}
                    handleItemSelection={(increment, isInput) => handleItemSelection(groupData, component.legacyId, component.name, component.id, increment, isInput)}
                    itemData={component}
                    disabled={disabled}
                    setSelectedOption={setSelectedOption}
                    setOpen={setOpen}
                    error={error[component.legacyId]}
                    handleRemoveError={handleRemoveError}
                    isCYOQtyButtonEnabled={isCYODefaultQtyButtonEnabled}
                />
            </div>
        ))
    );
    useEffect(() => {
        if (typeof data !== 'undefined' && typeof window !== 'undefined' && !window.wuCYOButtonQtyEventFired && data) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'CYO PDP V1',
                eventLabel: isCYOQtyButtonEnabled?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: isCYOQtyButtonEnabled?.toLowerCase() === 'test'  ? 'Variant' : 'Control',
                feature_element: 'CYO PDP V1',
                feature_category: 'Experiment',
            };
            selectedBYODispatch(trackEvent(eventParam));
            window.wuCYOButtonQtyEventFired = true;
        }
        return () => {
            window.wuCYOButtonQtyEventFired = false;
        };
    }, [data]);
    if (loading || variables?.skip) {
        return <GenericSkeleton />;
    }
    const required = () => (groupData?.minimumUnitsRequiredForComponent > 0 ? <span className={style.normalFont}> - Required</span> : '');
    return (
        <div
            className={style.cyoGroup}
        >
            {(!featureFlags['is-byo-mutiprogress-bar-enabled'] || maxGroupNumber?.length > 0)  && (
                <h4 variant="subtitle1" className={style.title}>
                    {ReactHtmlParser(groupData?.name?.toLowerCase())}{required()}
                </h4>
            )}
            {!maxGroupNumber?.length && featureFlags['is-byo-mutiprogress-bar-enabled'] && (
                <div>
                    <div className={style.mainContainer}>
                        <div className={style.titleContainer}>
                            <h4 variant="subtitle1" className={style.title}>
                                {ReactHtmlParser(groupData?.name?.toLowerCase())}{required()}
                            </h4>
                            <p className={style.subTitle}>{`(${selectedItems.groupProductSelected || 0} of ${groupData.numberofUnitsRequiredForComponent} added)`}</p>
                        </div>
                        {device === 'desktop' && clearSelection()}
                    </div>
                    <LinearProgressBar
                        total={groupData?.numberofUnitsRequiredForComponent}
                        selected={selectedItems?.groupProductSelected || 0}
                    />
                    {device !== 'desktop' && clearSelection()}
                </div>
            )}
            <div className={style.itemsContainer}>
                <Media query={{ maxWidth: 600 }} defaultMatches={deviceType === 'mobile'}>
                    {(matches) => (matches ? renderLayout()
                        : (
                            <Slider
                                ref={sliderRef}
                                className={style.sliderContainer}
                                draggable
                                dots={false}
                                infinite={false}
                                speed={500}
                                slidesToShow={3.5}
                                slidesToScroll={3}
                                arrows
                                style={{
                                    '--btn-selected-color': buttonColorSelect,
                                }}
                                nextArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M6.49 20.13l1.77 1.77 9.9-9.9-9.9-9.9-1.77 1.77L14.62 12l-8.13 8.13z" /></svg></div>}
                                prevArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.51 3.87L15.73 2.1 5.84 12l9.9 9.9 1.77-1.77L9.38 12l8.13-8.13z" /></svg></div>}
                                responsive={[
                                    {
                                        breakpoint: 600,
                                        settings: {
                                            slidesToShow: 2.75,
                                            slidesToScroll: 2,
                                        },
                                    },
                                ]}
                            >
                                {renderLayout()}
                            </Slider>
                        )
                    )}
                </Media>
            </div>
            {open && (
                <Dialog isOpen={open} title={(<span className={style.dialogTitle}>Item Details</span>)} onClose={onClose}>{renderDialog()}</Dialog>
            )}
        </div>
    );
};

BYOGroupSlider.propTypes = {
    groupData: object.isRequired,
    selectedItems: object.isRequired,
    handleItemSelection: func.isRequired,
    disabled: bool.isRequired,
    error: object.isRequired,
    handleRemoveError: func.isRequired,
    maxGroupNumber: array,
    selectedItem: object,
};
BYOGroupSlider.defaultProps = {
    maxGroupNumber: [],
    selectedItem: {},
};

export default BYOGroupSlider;
